import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";

export const SERVANT_LEADERSHIP_PACK: FrameworkPackDefinition = {
  id: "servant_leadership",
  label: "Servant Leadership",
  family: "coach_custom",
  source: "primary_source",
  preview: {
    philosophySummary:
      "Servant Leadership helps leaders use authority in service of people, growth, stewardship, ethical action, and shared ownership.",
    whatItNotices: [
      "Whether the leader is listening first and using authority to serve the work and people.",
      "Where empowerment, development, and stewardship are replacing control or status protection.",
      "Whether the leader's choices strengthen the capacity, dignity, and agency of others over time.",
    ],
    watchouts: [
      "Do not confuse servant leadership with avoiding hard calls or pleasing everyone.",
      "Do not frame service as self-erasure, martyrdom, or lack of authority.",
      "Do not infer virtue or motive beyond observable behavior.",
    ],
    sampleCoachLanguage: [
      "What would it mean to use your authority in service of their growth here?",
      "Where did you listen first, and where did you move into control too quickly?",
    ],
    sampleLeaderLanguage: [
      "Your coach would likely want you to notice whether authority created more agency for others.",
      "Through this lens, service is not softness. It is stewardship, listening, and growth made visible.",
    ],
  },
  runtime: {
    coreConstructs: [
      "Listening",
      "Empathy",
      "Healing",
      "Awareness",
      "Persuasion",
      "Conceptualization",
      "Foresight",
      "Stewardship",
      "Commitment to growth",
      "Community building",
      "Empowerment",
      "Ethical service",
    ],
    diagnosticQuestions: [
      "Where did the leader listen before directing, and what changed because they listened?",
      "How did the leader use authority: to control, to protect status, or to grow others' capacity?",
      "What act of stewardship would serve the person, the team, and the longer-term work?",
    ],
    interventionPatterns: [
      "Shift from control to empowerment while keeping accountability intact.",
      "Use listening to understand what growth or support the other person actually needs.",
      "Frame the next decision as stewardship of people, purpose, and long-term trust.",
    ],
    progressMarkers: [
      "The leader listens in a way that changes their response.",
      "People leave with more agency, clarity, and capacity than they had before.",
      "Authority is used to protect purpose, ethics, and growth rather than status.",
    ],
    stallSignals: [
      "The leader uses service language while retaining all control.",
      "The leader avoids a necessary decision in the name of being supportive.",
      "Helping becomes rescuing, preventing the other person from growing capacity.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "authority_in_service",
        label: "Authority in service",
        description: "Authority is used to increase agency, clarity, growth, or stewardship for others.",
        positiveIndicators: ["serve", "authority", "growth", "agency", "stewardship"],
        negativeIndicators: ["control", "status", "rescue", "avoid", "command"],
        evidenceHints: ["listened before deciding", "created ownership", "protected long-term trust"],
        prohibitedInferences: ["virtue judgment", "motive claim", "selflessness score"],
      },
      {
        id: "listening_to_empowerment_gap",
        label: "Listening to empowerment gap",
        description: "Listening is present but may not yet translate into empowerment or growth.",
        positiveIndicators: ["listen", "empathy", "support", "develop", "owner"],
        negativeIndicators: ["took over", "rescued", "unclear ownership", "dependency"],
        evidenceHints: ["what changed because they listened", "who owns the next step", "growth support"],
        prohibitedInferences: ["empathy level", "character claim", "servant identity"],
      },
    ],
    reflectionPatterns: [
      {
        id: "service_authority_and_growth",
        label: "Service, authority, and growth",
        promptTemplates: [
          "How did you use authority in this moment: to control, to rescue, or to build capacity?",
          "What did you hear because you listened first, and what changed because of it?",
          "What would stewardship of this person, team, and longer-term trust ask you to do next?",
        ],
        useWhen: ["authority_in_service", "listening_to_empowerment_gap"],
        avoidWhen: ["thin_evidence", "virtue_claim", "avoidant_support"],
      },
    ],
    boundaries: {
      allowed: ["listening", "agency", "growth", "stewardship", "ethical service", "empowerment"],
      prohibited: ["virtue label", "selflessness score", "martyrdom framing", "motive claim"],
      coachOnly: ["challenge rescuing patterns", "differentiate service from avoidance", "design empowerment practices"],
    },
    salienceWeights: {
      authority_in_service: 1.5,
      listening_to_empowerment_gap: 1.3,
    },
  },
};
